// Layer Utility Functions
// Helper functions for working with layers

import { LayerMetadata, LayerType } from './types';
import { ViewItem, Rectangle, Connector, TextBox, ModelItem } from 'src/types';

/**
 * Get the display name for a layer based on its type and data
 */
export function getLayerName(
  layer: ViewItem | Rectangle | Connector | TextBox,
  type: LayerType,
  modelItems?: ModelItem[]
): string {
  switch (type) {
    case LayerType.ITEM: {
      const viewItem = layer as ViewItem;
      const modelItem = modelItems?.find((item) => item.id === viewItem.id);
      return modelItem?.name || viewItem.id;
    }
    case LayerType.RECTANGLE: {
      const rect = layer as Rectangle;
      return rect.description || `Rectangle ${rect.id.slice(0, 8)}`;
    }
    case LayerType.CONNECTOR: {
      const conn = layer as Connector;
      return conn.description || `Connector ${conn.id.slice(0, 8)}`;
    }
    case LayerType.TEXTBOX: {
      const textBox = layer as TextBox;
      return textBox.content || `Text ${textBox.id.slice(0, 8)}`;
    }
    default:
      return 'Unknown Layer';
  }
}

/**
 * Convert view items to layer metadata
 */
export function viewItemsToLayers(
  viewItems: ViewItem[],
  modelItems: ModelItem[]
): LayerMetadata[] {
  return viewItems.map((item, index) => ({
    id: item.id,
    type: LayerType.ITEM,
    name: getLayerName(item, LayerType.ITEM, modelItems),
    visible: item.visible !== false, // Default to visible
    locked: item.locked || false,
    zIndex: item.zIndex ?? index, // Use stored zIndex or fall back to array index
  }));
}

/**
 * Convert rectangles to layer metadata
 */
export function rectanglesToLayers(rectangles: Rectangle[]): LayerMetadata[] {
  return rectangles.map((rect, index) => ({
    id: rect.id,
    type: LayerType.RECTANGLE,
    name: getLayerName(rect, LayerType.RECTANGLE),
    visible: rect.visible !== false, // Default to visible
    locked: rect.locked || false,
    zIndex: rect.zIndex ?? index, // Use stored zIndex or fall back to array index
  }));
}

/**
 * Convert connectors to layer metadata
 */
export function connectorsToLayers(connectors: Connector[]): LayerMetadata[] {
  return connectors.map((conn, index) => ({
    id: conn.id,
    type: LayerType.CONNECTOR,
    name: getLayerName(conn, LayerType.CONNECTOR),
    visible: conn.visible !== false, // Default to visible
    locked: conn.locked || false,
    zIndex: conn.zIndex ?? index, // Use stored zIndex or fall back to array index
  }));
}

/**
 * Convert textboxes to layer metadata
 */
export function textBoxesToLayers(textBoxes: TextBox[]): LayerMetadata[] {
  return textBoxes.map((textBox, index) => ({
    id: textBox.id,
    type: LayerType.TEXTBOX,
    name: getLayerName(textBox, LayerType.TEXTBOX),
    visible: textBox.visible !== false, // Default to visible
    locked: textBox.locked || false,
    zIndex: textBox.zIndex ?? index, // Use stored zIndex or fall back to array index
  }));
}

/**
 * Get all layers from a view
 */
export function getAllLayers(
  viewItems: ViewItem[],
  rectangles: Rectangle[],
  connectors: Connector[],
  textBoxes: TextBox[],
  modelItems: ModelItem[]
): LayerMetadata[] {
  const allLayers = [
    ...viewItemsToLayers(viewItems, modelItems),
    ...rectanglesToLayers(rectangles),
    ...connectorsToLayers(connectors),
    ...textBoxesToLayers(textBoxes),
  ];

  // Sort by zIndex (higher zIndex = higher in the list = rendered on top)
  return allLayers.sort((a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0));
}

/**
 * Get layer icon color based on type
 */
export function getLayerIconColor(type: LayerType): string {
  switch (type) {
    case LayerType.ITEM:
      return '#3b82f6'; // Blue
    case LayerType.CONNECTOR:
      return '#a855f7'; // Purple
    case LayerType.RECTANGLE:
      return '#10b981'; // Green
    case LayerType.TEXTBOX:
      return '#f59e0b'; // Amber
    default:
      return '#6b7280'; // Gray
  }
}

/**
 * Get layer icon name based on type
 */
export function getLayerIcon(type: LayerType): string {
  switch (type) {
    case LayerType.ITEM:
      return 'Component';
    case LayerType.CONNECTOR:
      return 'GitBranch';
    case LayerType.RECTANGLE:
      return 'Square';
    case LayerType.TEXTBOX:
      return 'Type';
    default:
      return 'Layers';
  }
}
